import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllCourses,
  getCourseById,
  createCourse,
  updateCourse,
  deleteCourse,
  getTeacherCourses,
  getStudentCourses,
  enrollInCourse,
  teachCourse
} from '../services/courseService';
import Notification from '../components/Common/Notification';

export const CourseContext = createContext();

export const CourseProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    if (!user && !storedUser) {
      console.error('User or token not available');
      return {};
    }

    const token = user?.token || JSON.parse(storedUser).token;
    return { Authorization: `Bearer ${token}` };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllCourses = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllCourses(authHeaders);
      setCourses(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching courses.';
      showNotification(errorMessage, true);
    }
  };

  const fetchCourseById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getCourseById(id, authHeaders);
      setSelectedCourse(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching course with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchTeacherCourses = async (teacherId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getTeacherCourses(teacherId, authHeaders);
      setCourses(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching courses for teacher with ID ${teacherId}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchStudentCourses = async (studentId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getStudentCourses(studentId, authHeaders);
      setCourses(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching courses for student with ID ${studentId}.`;
      showNotification(errorMessage, true);
    }
  };

  const addCourse = async (courseData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newCourse = await createCourse(courseData, authHeaders);
      setCourses(prevCourses => [...prevCourses, newCourse]);
      showNotification('Course added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding course.';
      showNotification(errorMessage, true);
    }
  };

  const editCourse = async (id, courseData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedCourse = await updateCourse(id, courseData, authHeaders);
      setCourses(prevCourses =>
        prevCourses.map(course => (course.id === id ? updatedCourse : course))
      );
      showNotification('Course updated successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating course.';
      showNotification(errorMessage, true);
    }
  };

  const removeCourse = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteCourse(id, authHeaders);
      setCourses(prevCourses => prevCourses.filter(course => course.id !== id));
      showNotification('Course removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing course.';
      showNotification(errorMessage, true);
    }
  };

  const enrollStudentInCourse = async (studentId, courseId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedCourse = await enrollInCourse(studentId, courseId, authHeaders);
      setCourses(prevCourses =>
        prevCourses.map(course => (course.id === courseId ? updatedCourse : course))
      );
      showNotification('Student enrolled successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error enrolling student.';
      showNotification(errorMessage, true);
    }
  };

  const assignTeacherToCourse = async (teacherId, courseId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedCourse = await teachCourse(teacherId, courseId, authHeaders);
      setCourses(prevCourses =>
        prevCourses.map(course => (course.id === courseId ? updatedCourse : course))
      );
      showNotification('Teacher assigned successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error assigning teacher.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllCourses();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <CourseContext.Provider
      value={{
        courses,
        selectedCourse,
        fetchAllCourses,
        fetchCourseById,
        fetchTeacherCourses,
        fetchStudentCourses,
        addCourse,
        editCourse,
        removeCourse,
        enrollStudentInCourse,
        assignTeacherToCourse,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </CourseContext.Provider>
  );
};
